import { useEffect, type ReactNode } from 'react'
import { supabase } from '@/shared/lib/supabase'
import { useAuthStore } from '@/features/auth/store/authStore'
import { fetchProfileWithMembership, seedDemoDataIfNeeded } from '@/features/auth/api/authApi'
import { fetchActivePass } from '@/features/wallet/api/accessPassApi'
import { queryClient } from '@/shared/lib/queryClient'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  useEffect(() => {
    const { setUser, setLoading, clear } = useAuthStore.getState()
    let isMounted = true

    // Safety timeout — if getSession() never resolves, clear loading after 5s
    const timeout = setTimeout(() => {
      if (isMounted) clear()
    }, 5000)

    // Initial session check
    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        if (!isMounted) return
        clearTimeout(timeout)
        if (session?.user) {
          try {
            let profile = await fetchProfileWithMembership(session.user.id)
            const seeded = await seedDemoDataIfNeeded(session.user.id)
            if (seeded) {
              profile = seeded
              await queryClient.invalidateQueries()
            }
            if (isMounted) setUser(session.user.id, profile)
            // Fetch wallet balance for points access
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- wallet tables not in generated types
            const { data: walletData } = await (supabase as any)
              .from('wallets')
              .select('balance')
              .eq('member_id', session.user.id)
              .maybeSingle()
            if (isMounted) {
              useAuthStore.getState().setWallet(walletData?.balance ?? null)
            }
            // Fetch active pass
            const passData = await fetchActivePass(session.user.id)
            if (isMounted) {
              useAuthStore.getState().setActivePass(passData)
            }
          } catch {
            if (isMounted) setUser(session.user.id, null)
          }
        } else {
          clear()
        }
      })
      .catch(() => {
        if (isMounted) clear()
        clearTimeout(timeout)
      })

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        clear()
        return
      }

      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user) {
        setLoading(true)
        fetchProfileWithMembership(session.user.id)
          .then(async (profile) => {
            const seeded = await seedDemoDataIfNeeded(session.user.id)
            if (seeded) await queryClient.invalidateQueries()
            if (isMounted) setUser(session.user.id, seeded ?? profile)
            // Fetch wallet balance for points access
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- wallet tables not in generated types
            const { data: walletData } = await (supabase as any)
              .from('wallets')
              .select('balance')
              .eq('member_id', session.user.id)
              .maybeSingle()
            if (isMounted) {
              useAuthStore.getState().setWallet(walletData?.balance ?? null)
            }
            // Fetch active pass
            const passData = await fetchActivePass(session.user.id)
            if (isMounted) {
              useAuthStore.getState().setActivePass(passData)
            }
          })
          .catch(() => { if (isMounted) setUser(session.user.id, null) })
          .finally(() => { if (isMounted) setLoading(false) })
      }
    })

    return () => {
      isMounted = false
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  return <>{children}</>
}
